'use client';

import { useEffect, useState } from 'react';
import Link from 'next/link';
import { fetchNearbyData } from '../../_lib/nearby/nearby.service';
import { NEARBY_CATEGORIES } from '../../_lib/nearby/nearby.config';
import type { NearbyCategoryKey, NearbyContext } from '../../_lib/nearby/nearby.types';
import { NearbyStripSkeleton } from './NearbyStripSkeleton';
import './Nearby.css';

interface NearbySectionProps {
  categoryKey: NearbyCategoryKey;
  ctx: NearbyContext;
}

/**
 * One category strip. Fetches independently of its sibling categories —
 * a slow or failing backend for one category never blocks the others
 * from rendering (each `NearbySection` owns its own request/loading
 * state). Rendering itself is delegated to that category's own
 * `renderCard` (see `nearby.config.tsx`) — this component never needs
 * to know what a sport/show/hotel/restaurant item actually looks like.
 */
export function NearbySection({ categoryKey, ctx }: NearbySectionProps) {
  const [items, setItems] = useState<unknown[]>([]);
  const [exploreHref, setExploreHref] = useState<string | undefined>();
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    fetchNearbyData(categoryKey, ctx).then((result) => {
      if (cancelled) return;
      setItems(result.items);
      setExploreHref(result.exploreHref);
      setLoading(false);
    });
    return () => {
      cancelled = true;
    };
  }, [
    categoryKey,
    ctx.latitude,
    ctx.longitude,
    ctx.cityName,
    ctx.countryCode,
    ctx.nationality,
    ctx.checkin,
    ctx.dateFrom,
    ctx.userCurrency,
    ctx.limit,
    ctx.eventId,
    ctx.referenceName,
  ]);

  if (loading) return <NearbyStripSkeleton />;
  if (!items.length) return null;

  const config = NEARBY_CATEGORIES[categoryKey];

  return (
    <section className="mt-5">
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h4 className="nearby-strip-title">{config.buildLabel(ctx.referenceName)}</h4>
        {/* Explore is a general "see more of this category" affordance
            — it points at the wider catalogue (e.g. all shows, all
            concerts in this city), so it stays visible whenever the
            adapter emits a link, even if the nearby fetch happened
            to return exactly `limit` items (`total === items.length`).
            The previous `total > items.length` guard hid the button
            for shows on every deploy where the theatre catalogue has
            few enough entries to fit inside the 4-item strip, which
            was the reported "Explore missing for Shows" bug. */}
        {items.length > 4 && exploreHref && (
          <Link
            href={exploreHref}
            className="btn btn-outline-primary"
            target="_blank"
            rel="noopener noreferrer"
          >
            Explore
          </Link>
        )}
      </div>

      {/* Render only the first 4 items per strip — the fetch pulls up
          to 10 (see `_lib/nearby/adapters/*` `limit: 10`) so the
          Explore gate `items.length > 4` above can decide whether
          there's more to browse without an extra "total" round-trip.
          The 5th–10th items live in state but never hit the DOM;
          they're the "there's more" signal, not a scroll target. */}
      <div className="row g-3">
        {items.slice(0, 4).map((item, idx) => (
          <div key={idx} className="col-12 col-sm-6 col-lg-3 mb-4 mb-lg-0 mb-md-3">
            {config.renderCard(item, idx, ctx)}
          </div>
        ))}
      </div>
    </section>
  );
}
